import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ShieldAlert } from 'lucide-react';
import { hasAtLeastRole } from '@portal/shared';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  Table,
  Tabs,
  useToast,
  type TableColumn,
} from '../../components/index.js';
import { useAuth } from '../../lib/auth-context.js';
import { api, ApiError } from '../../lib/api-client.js';

type Kind = 'description' | 'price' | 'value';

interface CorrectionRow {
  id: string;
  ts: string;
  userId: string;
  jobId: string;
  itemId: string;
  extras: Record<string, unknown>;
}

interface CorrectionsResponse {
  kind: Kind;
  rows: CorrectionRow[];
}

const TAB_LABEL: Record<Kind, string> = {
  description: 'Descriptions',
  price: 'Prices',
  value: 'Values',
};

const TAB_HELP: Record<Kind, string> = {
  description:
    'Every dictation correction Greg made to an item description. The most-repeated before→after pairs are the STT mistakes worth fixing.',
  price:
    'Engine-suggested vs. Greg-accepted prices, only where Greg overrode the suggestion. Systematic deltas point at retrieval/correction gaps in the pricing engine.',
  value:
    'Quantity and dictated-value edits. Less actionable than descriptions but useful for QA on STT number-parsing.',
};

export function Corrections() {
  const { me } = useAuth();
  const { toast } = useToast();
  const [kind, setKind] = useState<Kind>('description');
  const [rowsByKind, setRowsByKind] = useState<Partial<Record<Kind, CorrectionRow[]>>>({});
  const [loading, setLoading] = useState(false);
  const [grouped, setGrouped] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const canSee = !!me && hasAtLeastRole(me.role, 'admin');

  async function rebuildOverrides() {
    setRebuilding(true);
    try {
      const res = await api.post<{ count: number; keys: string[] }>(
        '/api/admin/brand-overrides/rebuild',
      );
      toast({
        title: 'Brand overrides rebuilt',
        description: `${res.count} brand${res.count === 1 ? '' : 's'} learned from edits. The STT engine picks these up on the next job.`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: 'Rebuild failed',
        description: err instanceof ApiError ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setRebuilding(false);
    }
  }

  useEffect(() => {
    if (!canSee) return;
    if (rowsByKind[kind]) return; // already fetched for this tab
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get<CorrectionsResponse>(
          `/api/admin/corrections?kind=${kind}&limit=200`,
        );
        if (cancelled) return;
        setRowsByKind((prev) => ({ ...prev, [kind]: res.rows }));
      } catch (err) {
        if (cancelled) return;
        toast({
          title: 'Failed to load corrections',
          description: err instanceof ApiError ? err.message : String(err),
          variant: 'danger',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, canSee, rowsByKind, toast]);

  if (!canSee) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto px-8 py-10">
          <EmptyState
            icon={ShieldAlert}
            title="Admin only"
            description="The activity log is restricted to admins and owners."
          />
        </div>
      </AppShell>
    );
  }

  const rows = rowsByKind[kind] ?? [];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-8 py-10 space-y-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Back to dashboard
        </Link>
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Activity log</h1>
            <p className="mt-2 text-base text-text-secondary max-w-3xl">
              Every dictation edit and manual price override across the portal. Use this to find
              patterns the STT and pricing engines should learn — repeated before→after pairs are
              the highest-leverage signal.
            </p>
          </div>
          <Button variant="secondary" onClick={rebuildOverrides} disabled={rebuilding}>
            {rebuilding ? 'Rebuilding…' : 'Rebuild brand overrides'}
          </Button>
        </header>

        <Tabs value={kind} onValueChange={(v) => setKind(v as Kind)} defaultValue="description">
          <TabList>
            {(['description', 'price', 'value'] as const).map((k) => (
              <Tab key={k} value={k}>
                {TAB_LABEL[k]}
                {rowsByKind[k] && (
                  <Badge className="ml-2" variant="neutral">
                    {rowsByKind[k]!.length}
                  </Badge>
                )}
              </Tab>
            ))}
          </TabList>

          <TabPanel value={kind}>
            <Card>
              <CardHeader
                title={TAB_LABEL[kind]}
                description={TAB_HELP[kind]}
                actions={
                  kind === 'description' && rows.length > 0 ? (
                    <Switch
                      label="Group identical pairs"
                      checked={grouped}
                      onChange={(e) => setGrouped(e.currentTarget.checked)}
                    />
                  ) : null
                }
              />
              <CardBody>
                {loading && rows.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Spinner />
                  </div>
                ) : rows.length === 0 ? (
                  <EmptyState
                    title="Nothing here yet"
                    description="As Greg edits items and overrides prices, those changes will appear here."
                  />
                ) : kind === 'description' ? (
                  <DescriptionTable rows={rows} grouped={grouped} />
                ) : kind === 'price' ? (
                  <PriceTable rows={rows} />
                ) : (
                  <ValueTable rows={rows} />
                )}
              </CardBody>
            </Card>
          </TabPanel>
        </Tabs>
      </div>
    </AppShell>
  );
}

// ---------- Tab tables -------------------------------------------------------

function DescriptionTable({ rows, grouped }: { rows: CorrectionRow[]; grouped: boolean }) {
  if (grouped) {
    const groups = groupByBeforeAfter(rows);
    const columns: TableColumn<GroupRow>[] = [
      { id: 'count', header: '#', cell: (g) => <span className="tabular-nums">{g.count}</span> },
      {
        id: 'pair',
        header: 'Before → After',
        cell: (g) => (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-text-secondary truncate">{g.before}</span>
            <ArrowRight className="size-3 text-text-tertiary shrink-0" aria-hidden />
            <span className="text-text-primary truncate">{g.after}</span>
          </div>
        ),
      },
      {
        id: 'sample',
        header: 'Latest source',
        cell: (g) => (
          <Link
            to={`/tools/jobs/${g.sample.jobId}`}
            className="text-sm text-accent-base hover:underline"
          >
            View job
          </Link>
        ),
      },
    ];
    return <Table columns={columns} rows={groups} rowKey={(g) => `${g.before}|${g.after}`} />;
  }

  const columns: TableColumn<CorrectionRow>[] = [
    { id: 'ts', header: 'When', cell: (r) => <TimeCell ts={r.ts} /> },
    {
      id: 'pair',
      header: 'Before → After',
      cell: (r) => (
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-text-secondary truncate">{String(r.extras.before ?? '')}</span>
          <ArrowRight className="size-3 text-text-tertiary shrink-0" aria-hidden />
          <span className="text-text-primary truncate">{String(r.extras.after ?? '')}</span>
        </div>
      ),
    },
    { id: 'job', header: '', cell: (r) => <JobLink jobId={r.jobId} /> },
  ];
  return <Table columns={columns} rows={rows} rowKey={(r) => r.id} />;
}

function PriceTable({ rows }: { rows: CorrectionRow[] }) {
  const columns: TableColumn<CorrectionRow>[] = [
    { id: 'ts', header: 'When', cell: (r) => <TimeCell ts={r.ts} /> },
    {
      id: 'engine',
      header: 'Engine GC',
      cell: (r) => <span className="tabular-nums">{fmt(r.extras.suggestedGc)}</span>,
    },
    {
      id: 'accepted',
      header: 'Accepted',
      cell: (r) => (
        <span className="tabular-nums text-text-primary">{fmt(r.extras.acceptedPrice)}</span>
      ),
    },
    {
      id: 'delta',
      header: 'Δ',
      cell: (r) => {
        const a = num(r.extras.acceptedPrice);
        const g = num(r.extras.suggestedGc);
        if (a == null || g == null) return <span className="text-text-tertiary">—</span>;
        const d = a - g;
        return (
          <span
            className={
              'tabular-nums ' +
              (d > 0 ? 'text-success ' : d < 0 ? 'text-danger ' : 'text-text-tertiary ')
            }
          >
            {d > 0 ? '+' : ''}
            {fmt(d)}
          </span>
        );
      },
    },
    { id: 'job', header: '', cell: (r) => <JobLink jobId={r.jobId} /> },
  ];
  return <Table columns={columns} rows={rows} rowKey={(r) => r.id} />;
}

function ValueTable({ rows }: { rows: CorrectionRow[] }) {
  const columns: TableColumn<CorrectionRow>[] = [
    { id: 'ts', header: 'When', cell: (r) => <TimeCell ts={r.ts} /> },
    {
      id: 'field',
      header: 'Field',
      cell: (r) => <Badge variant="neutral">{String(r.extras.field ?? '')}</Badge>,
    },
    {
      id: 'pair',
      header: 'Before → After',
      cell: (r) => (
        <div className="flex items-center gap-2 tabular-nums">
          <span className="text-text-secondary">{fmtRaw(r.extras.before)}</span>
          <ArrowRight className="size-3 text-text-tertiary shrink-0" aria-hidden />
          <span className="text-text-primary">{fmtRaw(r.extras.after)}</span>
        </div>
      ),
    },
    { id: 'job', header: '', cell: (r) => <JobLink jobId={r.jobId} /> },
  ];
  return <Table columns={columns} rows={rows} rowKey={(r) => r.id} />;
}

// ---------- Helpers ----------------------------------------------------------

interface GroupRow {
  before: string;
  after: string;
  count: number;
  sample: CorrectionRow;
}

function groupByBeforeAfter(rows: CorrectionRow[]): GroupRow[] {
  const map = new Map<string, GroupRow>();
  for (const r of rows) {
    const before = String(r.extras.before ?? '');
    const after = String(r.extras.after ?? '');
    const key = `${before}${after}`;
    const existing = map.get(key);
    if (existing) existing.count += 1;
    else map.set(key, { before, after, count: 1, sample: r });
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

function TimeCell({ ts }: { ts: string }) {
  const formatted = useMemo(() => {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  }, [ts]);
  return <span className="text-sm text-text-secondary">{formatted}</span>;
}

function JobLink({ jobId }: { jobId: string }) {
  return (
    <Link to={`/tools/jobs/${jobId}`} className="text-sm text-accent-base hover:underline">
      Job
    </Link>
  );
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function fmt(v: unknown): string {
  const n = num(v);
  if (n == null) return '—';
  return n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
}

function fmtRaw(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}
